import * as React from "react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { CircularProgress, Grid, MenuItem, TextField, Autocomplete } from '@mui/material'
import { CouponModel, CouponRequestModel, CouponType, saveCoupon, updateCoupon } from "../../../../services/coupon/coupon.services";
import { handlePromise } from "../../../../utils/handlePromisse";
import { getPaginatedUsers } from "../../../../services/userService";
import { UserModel } from "../../users/table/user.model";
import debounce from 'lodash/debounce';
import CurrencyTextField from "../../../../components/currency-text-field";
import PercentageTextField from "../../../../components/percentage-text-field";
import DateTextField from "../../../../components/date-text-field";
import { SimpleSnackbar } from "../../../../components/snackbar";
import moment from "moment";

export type CouponFormHandle = {
    submit: () => void;
}

type DiscountType = 'VALUE' | 'PERCENTAGE';

type Props = {
    onCouponSaved: () => void;
    initialCoupon?: CouponModel;
}

const EMPTY_STATE: CouponRequestModel = {
    code: '',
    type: 'GENERAL',
    quantity: undefined,
    value: undefined,
    percentage: undefined,
    expirationDate: undefined,
    userId: undefined,
};

function inferDiscountType(coupon?: CouponModel): DiscountType {
    if (!coupon) return 'VALUE';
    if (coupon.percentage !== null && coupon.percentage !== undefined) return 'PERCENTAGE';
    return 'VALUE';
}

function inferExpirationDate(coupon?: CouponModel): string | undefined {
    if (!coupon?.expiration_date) return undefined;
    return moment(coupon.expiration_date).utcOffset(-180).format('DD/MM/YYYY');
}

function couponToRequest(coupon: CouponModel): CouponRequestModel {
    return {
        code: coupon.code,
        type: (coupon.type as CouponType) ?? 'GENERAL',
        quantity: coupon.quantity ?? undefined,
        value: coupon.value ? parseFloat(String(coupon.value)) : undefined,
        percentage: coupon.percentage !== null && coupon.percentage !== undefined
            ? String(coupon.percentage) + '%'
            : undefined,
        expirationDate: inferExpirationDate(coupon),
        userId: undefined,
    };
}

export const CouponForm = forwardRef<CouponFormHandle, Props>(({ onCouponSaved, initialCoupon }, ref) => {
    const isEdit = !!initialCoupon;

    const [state, setState] = useState<CouponRequestModel>(
        initialCoupon ? couponToRequest(initialCoupon) : EMPTY_STATE
    );
    const [discountType, setDiscountType] = useState<DiscountType>(inferDiscountType(initialCoupon));

    const [users, setUsers] = useState<UserModel[]>([]);
    const [loading, setLoading] = useState(false);

    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    // Re-initialize when the modal switches between create and edit
    useEffect(() => {
        if (initialCoupon) {
            setState(couponToRequest(initialCoupon));
            setDiscountType(inferDiscountType(initialCoupon));
        } else {
            setState(EMPTY_STATE);
            setDiscountType('VALUE');
        }
    }, [initialCoupon]);

    const handleChange = (field: keyof CouponRequestModel) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setState(prevState => {
            const newState = { ...prevState, [field]: value };
            if (field === 'type') {
                return clearIrrelevantFields(newState);
            }
            return newState;
        });
    };

    const handleDiscountTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const next = event.target.value as DiscountType;
        setDiscountType(next);
        // clear the field that is no longer relevant
        setState(prev => ({
            ...prev,
            value: next === 'VALUE' ? prev.value : undefined,
            percentage: next === 'PERCENTAGE' ? prev.percentage : undefined,
        }));
    };

    const clearIrrelevantFields = (s: CouponRequestModel): CouponRequestModel => {
        switch (s.type) {
            case 'GENERAL':
                return { ...s, quantity: undefined, expirationDate: undefined, userId: undefined };
            case 'QUANTITY':
                return { ...s, expirationDate: undefined, userId: undefined };
            case 'SPECIFIC':
                return { ...s, quantity: undefined, expirationDate: undefined };
            case 'EXPIRATION':
                return { ...s, quantity: undefined, userId: undefined };
            default:
                return s;
        }
    };

    const fetchUsers = useCallback(debounce(async (query: string) => {
        if (!query || query.length < 3) {
            setUsers([]);
            return;
        }
        setLoading(true);
        const [result, error] = await handlePromise(getPaginatedUsers({ query, page: 1, size: 10 }));

        if (!!error) {
            console.error(error.response.data.message);
            setLoading(false);
            return;
        }

        setUsers(result.data.data);
        setLoading(false);
    }, 500), []);

    const validate = (): string | null => {
        if (!state.code || !state.code.trim()) {
            return "Código é obrigatório.";
        }
        if (state.type === 'QUANTITY' && !state.quantity) {
            return "Quantidade de usos é obrigatória para cupons de quantidade.";
        }
        if (state.type === 'SPECIFIC' && !state.userId) {
            return "Usuário é obrigatório para cupons de usuário.";
        }
        if (state.type === 'EXPIRATION' && !state.expirationDate) {
            return "Data de expiração é obrigatória para cupons de expiração.";
        }
        if (discountType === 'VALUE' && !state.value) {
            return "Valor é obrigatório.";
        }
        if (discountType === 'PERCENTAGE' && !state.percentage) {
            return "Percentual é obrigatório.";
        }
        return null;
    };

    const buildPayload = (): CouponRequestModel => {
        const pct = discountType === 'PERCENTAGE' && state.percentage
            ? parseFloat(state.percentage.replace('%', '')).toString()
            : undefined;
        const val = discountType === 'VALUE' && state.value
            ? parseFloat(state.value.toString())
            : undefined;

        let expirationDate: any = undefined;
        if (state.type === 'EXPIRATION' && state.expirationDate) {
            // DateTextField gives us DD/MM/YYYY; convert to ISO for the backend
            const parts = state.expirationDate.split('/');
            if (parts.length === 3) {
                expirationDate = new Date(
                    parseInt(parts[2], 10),
                    parseInt(parts[1], 10) - 1,
                    parseInt(parts[0], 10),
                );
            } else {
                expirationDate = new Date(state.expirationDate);
            }
        }

        return {
            code: state.code.trim().toUpperCase(),
            type: state.type,
            quantity: state.type === 'QUANTITY' && state.quantity
                ? parseInt(state.quantity.toString(), 10)
                : undefined,
            userId: state.type === 'SPECIFIC' ? state.userId : undefined,
            expirationDate,
            value: val,
            percentage: pct,
        };
    };

    const onSave = useCallback(async () => {
        const errorMessage = validate();
        if (errorMessage) {
            setSnackbarMessage(errorMessage);
            setShowSnackbar(true);
            return;
        }

        setLoading(true);
        const payload = buildPayload();

        const apiCall = isEdit && initialCoupon
            ? updateCoupon(initialCoupon.id, payload)
            : saveCoupon(payload);

        const [_, error] = await handlePromise(apiCall);
        if (!!error) {
            setSnackbarMessage(error.response?.data?.message ?? 'Erro ao salvar cupom.');
            setShowSnackbar(true);
            setLoading(false);
            return;
        }

        onCouponSaved();

        if (!isEdit) {
            setState(EMPTY_STATE);
            setDiscountType('VALUE');
        }

        setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, discountType, isEdit, initialCoupon, onCouponSaved]);

    useImperativeHandle(ref, () => ({
        submit: onSave,
    }), [onSave]);

    return (
        <>
            <SimpleSnackbar
                show={showSnackbar}
                message={snackbarMessage}
                onDismiss={() => setShowSnackbar(false)}
            />

            <Grid container spacing={2}>
                {/* Row 1: Código + Tipo */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Código"
                        placeholder="EX: BEMVINDO20"
                        variant="outlined"
                        value={state.code}
                        onChange={handleChange('code')}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        select
                        label="Tipo"
                        variant="outlined"
                        value={state.type}
                        onChange={handleChange('type')}
                    >
                        <MenuItem value="GENERAL">Geral</MenuItem>
                        <MenuItem value="QUANTITY">Quantidade</MenuItem>
                        <MenuItem value="SPECIFIC">Usuário</MenuItem>
                        <MenuItem value="EXPIRATION">Expiração</MenuItem>
                    </TextField>
                </Grid>

                {/* Conditional field based on Tipo */}
                {state.type === 'QUANTITY' && (
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Quantidade de usos"
                            placeholder="nº máximo de usos"
                            variant="outlined"
                            type="number"
                            value={state.quantity ?? ''}
                            onChange={handleChange('quantity')}
                            inputProps={{ min: 1 }}
                        />
                    </Grid>
                )}
                {state.type === 'SPECIFIC' && (
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            options={users}
                            getOptionLabel={(option) => `${option.name} — ${option.email}`}
                            loading={loading}
                            onInputChange={(_, value) => fetchUsers(value)}
                            onChange={(_, value) => setState(prev => ({ ...prev, userId: value?.id }))}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Usuário"
                                    placeholder="Buscar usuário por nome ou e-mail"
                                    variant="outlined"
                                    fullWidth
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loading ? <CircularProgress color="inherit" size={20}/> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>
                )}
                {state.type === 'EXPIRATION' && (
                    <Grid item xs={12} sm={6}>
                        <DateTextField
                            label="Data de expiração"
                            value={state.expirationDate}
                            onChange={(value) => setState(prev => ({ ...prev, expirationDate: value }))}
                            sx={{ width: '100%' }}
                        />
                    </Grid>
                )}

                {/* Row: Tipo de desconto */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        select
                        label="Tipo de desconto"
                        variant="outlined"
                        value={discountType}
                        onChange={handleDiscountTypeChange}
                    >
                        <MenuItem value="VALUE">Valor (R$)</MenuItem>
                        <MenuItem value="PERCENTAGE">Percentual (%)</MenuItem>
                    </TextField>
                </Grid>

                {/* Conditional discount field */}
                {discountType === 'VALUE' && (
                    <Grid item xs={12} sm={6}>
                        <CurrencyTextField
                            label="Valor (R$)"
                            value={state.value}
                            onChange={(value) => setState(prev => ({ ...prev, value }))}
                            sx={{ width: '100%' }}
                        />
                    </Grid>
                )}
                {discountType === 'PERCENTAGE' && (
                    <Grid item xs={12} sm={6}>
                        <PercentageTextField
                            label="Percentual (%)"
                            value={state.percentage}
                            onChange={(value) => setState(prev => ({ ...prev, percentage: value }))}
                            sx={{ width: '100%' }}
                        />
                    </Grid>
                )}
            </Grid>
        </>
    );
});

CouponForm.displayName = 'CouponForm';
